import { useTheme } from '@emotion/react';
import { Formik, FormikProps } from 'formik';
import React, { forwardRef } from 'react';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  EntryList,
  FieldError,
  Icon,
  Text,
  TextField,
  TextFieldGroup,
} from '~/components/blocks';
import { PasswordRuleChecklist } from '~/components/partials/PasswordRuleChecklist';
import { useFetchPasswordPolicy } from '~/hooks/use-fetch-password-policy';
import { passwordPlaceholder } from '~/utils/password';

import { Fields } from './types';
import { validationSchema, validationUpdateSchema } from './validation';

const ROLES = [
  {
    role: 'admin',
    label: '店舗管理',
  },
  {
    role: 'pharmacist',
    label: '服薬指導',
  },
  {
    role: 'company_admin',
    label: '本部管理',
  },
] as const;

type Props = {
  initialValues: Fields;
  disabled: boolean;
  privilege: boolean;
  creatable?: boolean;
  updatedAuth?: boolean;
  username?: string;
  onResetPassword?: () => void;
  onSubmit: (values: Fields) => void;
};

export const PractitionerForm = forwardRef<FormikProps<Fields>, Props>((props, ref) => {
  const theme = useTheme();
  const {
    disabled,
    privilege,
    creatable = false,
    updatedAuth = false,
    onResetPassword,
    onSubmit,
  } = props;
  const isResetPassword = onResetPassword !== undefined;
  const { policy, loading: policyLoading } = useFetchPasswordPolicy();

  return (
    <Formik
      enableReinitialize
      innerRef={ref}
      initialValues={props.initialValues}
      validationSchema={creatable ? validationSchema : validationUpdateSchema(policy)}
      onSubmit={(values) => onSubmit(values)}
    >
      {(formik) => (
        <Box paddingX={theme.space.xl}>
          <EntryList>
            <EntryList.Head>氏名</EntryList.Head>
            <EntryList.Body>
              <TextFieldGroup>
                <TextField
                  disabled={disabled}
                  name="familyName"
                  error={!!formik.errors.familyName}
                  value={formik.values.familyName}
                  onChange={formik.handleChange}
                  placeholder="薬局"
                />
                <TextField
                  disabled={disabled}
                  name="givenName"
                  error={!!formik.errors.givenName}
                  value={formik.values.givenName}
                  onChange={formik.handleChange}
                  placeholder="太郎"
                />
              </TextFieldGroup>
              {(formik.errors.familyName || formik.errors.givenName) && (
                <FieldError error={(formik.errors.familyName || formik.errors.givenName)!} />
              )}
            </EntryList.Body>
          </EntryList>
          <EntryList>
            <EntryList.Head>氏名（カナ）</EntryList.Head>
            <EntryList.Body>
              <TextFieldGroup>
                <TextField
                  disabled={disabled}
                  name="phoneticFamilyName"
                  error={!!formik.errors.phoneticFamilyName}
                  value={formik.values.phoneticFamilyName}
                  onChange={formik.handleChange}
                  placeholder="ヤッキョク"
                />
                <TextField
                  disabled={disabled}
                  name="phoneticGivenName"
                  error={!!formik.errors.phoneticGivenName}
                  value={formik.values.phoneticGivenName}
                  onChange={formik.handleChange}
                  placeholder="タロウ"
                />
              </TextFieldGroup>
              {(formik.errors.phoneticFamilyName || formik.errors.phoneticGivenName) && (
                <FieldError
                  error={(formik.errors.phoneticFamilyName || formik.errors.phoneticGivenName)!}
                />
              )}
            </EntryList.Body>
          </EntryList>
          <EntryList>
            <EntryList.Head>ユーザーID</EntryList.Head>
            <EntryList.Body>
              {creatable ? (
                <>
                  <TextField
                    disabled={disabled}
                    name="username"
                    error={formik.errors.username}
                    value={formik.values.username}
                    onChange={formik.handleChange}
                    placeholder="半角英数6文字以上"
                  />
                  <Text size="xs" marginTop={theme.space.m}>
                    ※ ユーザーIDは一度作成すると変更できません
                  </Text>
                </>
              ) : (
                <Text>{props.username ?? 'ー'}</Text>
              )}
            </EntryList.Body>
          </EntryList>
          {updatedAuth && (
            <>
              <EntryList>
                <EntryList.Head>パスワード</EntryList.Head>
                <EntryList.Body>
                  <TextField
                    disabled={disabled}
                    name="password"
                    type="password"
                    error={!!formik.errors.password}
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    placeholder={passwordPlaceholder(policy)}
                  />
                  {!policyLoading && (
                    <Box marginTop={theme.space.s}>
                      <PasswordRuleChecklist
                        password={formik.values.password ?? ''}
                        policy={policy}
                      />
                    </Box>
                  )}
                </EntryList.Body>
              </EntryList>
              <EntryList>
                <EntryList.Head>パスワード（確認）</EntryList.Head>
                <EntryList.Body>
                  <TextField
                    disabled={disabled}
                    type="password"
                    name="passwordConfirmation"
                    error={formik.errors.passwordConfirmation}
                    value={formik.values.passwordConfirmation}
                    onChange={formik.handleChange}
                    placeholder={passwordPlaceholder(policy)}
                  />
                </EntryList.Body>
              </EntryList>
            </>
          )}
          {isResetPassword && (
            <Box marginTop={theme.space.l}>
              <Button onClick={onResetPassword}>
                <Icon size="l" icon="pw-reset" />
                パスワード再設定
              </Button>
            </Box>
          )}
          <Box marginTop={theme.space.xl}>
            <CheckboxGroup orientation="vertical">
              {ROLES.map(({ role, label }, idx) => {
                if (!privilege && role === 'company_admin') return null;

                return (
                  <Checkbox
                    key={idx}
                    disabled={disabled}
                    label={label}
                    checked={formik.values.roles.includes(role)}
                    onChange={() => {
                      const roles = formik.values.roles.concat();
                      if (roles.includes(role)) {
                        formik.setFieldValue(
                          'roles',
                          roles.filter((r) => r !== role),
                        );
                      } else {
                        roles.push(role);
                        formik.setFieldValue('roles', roles);
                      }
                    }}
                  />
                );
              })}
            </CheckboxGroup>
            {privilege && (
              <Alert mt={theme.space.m} status="error">
                <Text size="s">
                  本部設定へのアクセスが許可されるため、特定のスタッフのみ許可することを推奨します。
                </Text>
              </Alert>
            )}
          </Box>
        </Box>
      )}
    </Formik>
  );
});

PractitionerForm.displayName = 'PractitionerForm';
